import './objectTypeAndPolicies.less'
import {
  ACTION_DATABASES_AND_OBJECT_TYPE,
  DataSourceParentObjectTypes,
  fetchObjectTypeAndPolicies,
  ObjectTypeAndPoliciesParams,
  reset
} from './objectTypeAndPoliciesSlice'
import { RootState } from '../../rootReducer'
import useUrlContext from '../../hooks/useUrlContext'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  FILTER_DANGER_KEY,
  GRAPHQL_API_FILTERS,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_BLOB,
  URL_DATA_SOURCES_STRUCTURED,
  URL_POLICIES,
  URL_RULE_SET
} from '../../constants'
import { getShowLoader } from '../../reducers/requestReducer'
import LoaderView from '../../components/Loader/loader'
import SvgIconPolicies from '../../assets/inlineSvg/icon-policies.svg'
import SvgBriefcase from '../../assets/inlineSvg/briefcase.svg'
import SvgIconLayout from '../../assets/inlineSvg/icon-banner.svg'
import SvgIconType from '../../assets/inlineSvg/icon-object-type.svg'
import { getUpdatedUrl } from '../../utils/urlUtil'
import { getFeatureConfig } from '../../configs/featureConfig'
import { mockObjectTypeAndPoliciesWidgetData } from '../dashboards/renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../dashboards/mainDashboardv2/constants'
import { getDataSourceBasePath } from '../../utils/dataSourceUtil'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import React, { useEffect } from 'react'
import { Typography } from '@lightbeamai/design-system'

interface OwnProps {
  className?: string
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
  widgetDashboardId?: string
}

interface StateProps {
  objectTypeCount?: number
  policiesCount?: number
  dataExtensionsCount?: number
  businessUnitsCount?: number
  parentObjectType?: string
  showLoader: boolean
}

interface DispatchProps {
  fetchObjectTypeAndPolicies: (params: ObjectTypeAndPoliciesParams) => void
  reset: () => void
}

type ObjectTypeAndPoliciesComponentProps = OwnProps & StateProps & DispatchProps

const ObjectTypeAndPolicies = (props: ObjectTypeAndPoliciesComponentProps): React.ReactElement => {
  const {
    objectTypeCount = 0,
    policiesCount = 0,
    dataExtensionsCount = 0,
    businessUnitsCount = 0,
    parentObjectType = '',
    className = '',
    showLoader,
    useMockData = false,
    widgetDatasourceId,
    widgetDatasourceType,
    widgetDashboardId,
    fetchObjectTypeAndPolicies,
    reset
  } = props

  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = widgetDatasourceId || context.contextFilters[DATA_SOURCE_ID]
  const dataSourceType =
    widgetDatasourceType || (context.queryParams[DATA_SOURCE_TYPE] as DATA_SOURCE_TYPES)

  const config = getFeatureConfig(dataSourceType).dashboard.widgetObjectTypeAndPolicies

  useEffect(() => {
    if (useMockData) return
    if (datasourceId) {
      const filters = { [GRAPHQL_API_FILTERS.dataSourceIds]: [datasourceId] }
      if (config.filterCategory) {
        filters[GRAPHQL_API_FILTERS.category] = [config.filterCategory]
      }
      fetchObjectTypeAndPolicies({
        datasourceId,
        filters,
        [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
      })
    }
  }, [datasourceId, config.filterCategory, fetchObjectTypeAndPolicies, useMockData])

  useEffect(() => () => reset(), [reset])

  const pathBaseString = getDataSourceBasePath(dataSourceType, datasourceId, context.pathname)

  const linkBlobsCardView = getUpdatedUrl({
    pathName: pathBaseString + URL_BLOB,
    pageName: URL_BLOB,
    resetPageQuery: true,
    pageQueryParams: config.urlFilterDander ? { [FILTER_DANGER_KEY]: config.urlFilterDander } : {},
    queryParams: {
      ...(dataSourceType && { [DATA_SOURCE_TYPE]: dataSourceType }),
      ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
    }
  })
  const linkPolicies = getUpdatedUrl({
    pageName: URL_RULE_SET,
    pathName: URL_POLICIES + URL_RULE_SET,
    resetPageQuery: true,
    pageQueryParams: { [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list }
  })

  let textParentObjectType = ''
  if (parentObjectType === DataSourceParentObjectTypes.customer) {
    textParentObjectType = intl.formatMessage({ id: 'dashboard.dataSource.widget.text.customers' })
  }
  if (parentObjectType === DataSourceParentObjectTypes.organization) {
    textParentObjectType = intl.formatMessage({
      id: 'dashboard.dataSource.widget.text.organizations'
    })
  }
  if (parentObjectType === DataSourceParentObjectTypes.incidents) {
    textParentObjectType = intl.formatMessage({ id: 'dashboard.dataSource.widget.text.incidents' })
  }

  const blobsListUrl = getUpdatedUrl({
    pathName: `${URL_DATA_SOURCES_STRUCTURED}/${datasourceId}` + URL_BLOB,
    pageName: URL_BLOB,
    queryParams: {
      [DATA_SOURCE_TYPE]: dataSourceType
    },
    pageQueryParams: { [FILTER_DANGER_KEY]: intl.formatMessage({ id: 'filters.sensitive' }) }
  })

  return (
    <div className="lb-widget-card xs-p-16">
      <div
        className={`databases-and-object-types ${className}`}
        data-test-id="objecttypeandpolicies-object-type-and-policies"
      >
        <Typography className="font-600 widget-header" as="div">
          {intl.formatMessage({ id: 'dataSources.widget.overview.title' })}
        </Typography>
        <div className="database-count">
          {dataSourceType === DATA_SOURCE_TYPES.salesForceMarketingCloud ? (
            <>
              <Link to={blobsListUrl} className="widget">
                <div className="icon-box">
                  <SvgIconLayout style={{ color: '#6a737d' }} />
                </div>
                <Typography
                  as="div"
                  color="primary"
                  variant="basePlus2"
                  className="count xs-pt-8 xs-m-0 font-600"
                >
                  {intl.formatNumber(dataExtensionsCount, { notation: 'compact' })}
                </Typography>
                <Typography
                  as="div"
                  variant="baseMinus2"
                  color="secondary"
                  className="text font-500 xs-mt-4"
                >
                  {intl.formatMessage({ id: 'dashboard.dataSource.widget.text.dataExtensions' })}
                </Typography>
              </Link>
              <div className="divider" />
              <Link to={blobsListUrl} className="widget">
                <div className="icon-box">
                  <SvgBriefcase style={{ stroke: '#6a737d' }} />
                </div>
                <Typography
                  as="div"
                  color="primary"
                  variant="basePlus2"
                  className="count xs-pt-8 xs-m-0 font-600"
                >
                  {intl.formatNumber(businessUnitsCount, { notation: 'compact' })}
                </Typography>
                <Typography
                  as="div"
                  variant="baseMinus2"
                  color="secondary"
                  className="text font-500 xs-mt-4"
                >
                  {intl.formatMessage({ id: 'dashboard.dataSource.widget.text.businessUnits' })}
                </Typography>
              </Link>
              <div className="divider" />
            </>
          ) : (
            parentObjectType && (
              <>
                <Link to={linkBlobsCardView} className="widget">
                  <div className="icon-box">
                    <SvgIconType />
                  </div>
                  <Typography
                    as="div"
                    color="primary"
                    variant="basePlus2"
                    className="count xs-pt-8 xs-m-0 font-600"
                  >
                    {intl.formatNumber(objectTypeCount, { notation: 'compact' })}
                  </Typography>
                  <Typography
                    as="div"
                    variant="baseMinus2"
                    color="secondary"
                    className="text font-500 xs-mt-4"
                  >
                    {dataSourceType === DATA_SOURCE_TYPES.workday
                      ? intl.formatMessage({ id: 'dashboard.dataSource.widget.text.worker' })
                      : textParentObjectType}
                  </Typography>
                </Link>
                <div className="divider" />
              </>
            )
          )}
          <Link to={linkPolicies} className="widget">
            <div className="icon-box">
              <SvgIconPolicies />
            </div>
            <Typography
              as="div"
              color="primary"
              variant="basePlus2"
              className="count xs-pt-8 xs-m-0 font-600"
            >
              {intl.formatNumber(policiesCount, { notation: 'compact' })}
            </Typography>
            <Typography
              as="div"
              variant="baseMinus2"
              color="secondary"
              className="text font-500 xs-mt-4"
            >
              {intl.formatMessage({ id: 'dashboard.dataSource.widget.text.policies' })}
            </Typography>
          </Link>
        </div>
        <LoaderView showLoader={showLoader} size="small" />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const uniqueWidgetIndex = getUniqueWidgetIndex(
    ownProps.widgetDashboardId,
    ownProps.widgetDatasourceId
  )
  return {
    businessUnitsCount: ownProps?.useMockData
      ? mockObjectTypeAndPoliciesWidgetData.businessUnitsCount
      : state.objectTypeAndPolicies[uniqueWidgetIndex]?.businessUnitsCount,
    dataExtensionsCount: ownProps?.useMockData
      ? mockObjectTypeAndPoliciesWidgetData.dataExtensionsCount
      : state.objectTypeAndPolicies[uniqueWidgetIndex]?.dataExtensionsCount,
    objectTypeCount: ownProps?.useMockData
      ? mockObjectTypeAndPoliciesWidgetData.objectTypeCount
      : state.objectTypeAndPolicies[uniqueWidgetIndex]?.parentObjectCount,
    policiesCount: ownProps?.useMockData
      ? mockObjectTypeAndPoliciesWidgetData.policiesCount
      : state.objectTypeAndPolicies[uniqueWidgetIndex]?.policiesCount,
    parentObjectType: ownProps?.useMockData
      ? mockObjectTypeAndPoliciesWidgetData.parentObjectType
      : state.objectTypeAndPolicies[uniqueWidgetIndex]?.parentObjectType,
    showLoader: getShowLoader(ACTION_DATABASES_AND_OBJECT_TYPE)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchObjectTypeAndPolicies,
  reset
}

export default connect(mapStateToProps, mapDispatchToProps)(ObjectTypeAndPolicies)
